// program_share_ui.jsx — share a program (link + QR), paste a link to import,
// and the preview sheet a shared link opens ("Add to my programs").
//
//   <ShareProgramSheet open program onClose>
//   <ImportLinkSheet open onClose onPreview(token)>
//   <SharedProgramSheet token onClose onAdded(programId)>
//   useSharedLinkToken() → [token | null, clear]   (the '#p=…' fragment of this page)

import React from 'react';
import { LLSheet } from './sheets.jsx';
import { useApp } from './store.jsx';
import { LL, LLIcon } from './tokens.jsx';
import { qrMatrix, qrSvgPath } from './qr.js';
import { encodeProgramLink, decodeProgramToken, tokenFromText, materializeShared, sharedProgramKey, findSharedCopy } from './program_share.js';
import { useSharedLinkPending, clearSharedToken } from './share_link.js';
import { catalogToExercise, loadCatalog, useCatalog, catalogEntryFor } from './exercise_catalog.js';
import { resolveTemplateExercise } from './program_templates.js';
import { SCHEMES, schemeOf, prescribe, rowsSummary, cycleOf } from './progression.js';
import { ExerciseThumb } from './exercise_picker.jsx';
import { Sheet, Btn, Pill, StatTile, SplitStrip, kicker, card, meta, fmtRest, fmtReps, PIcon } from './program_ui.jsx';

// ——— Clipboard / share ————————————————————————————————————————————————————

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* fall back */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}
const canShare = () => typeof navigator !== 'undefined' && typeof navigator.share === 'function';

// ——— The '#p=' fragment ——————————————————————————————————————————————————

// The token is captured once and stripped from the URL by share_link.js (before
// any sheet pushes a history entry), so closing the preview never re-opens it.
export function useSharedLinkToken() {
  const token = useSharedLinkPending();
  return [token, clearSharedToken];
}

// ——— QR ————————————————————————————————————————————————————————————————————

function QrCode({ text, size = 232 }) {
  const qr = React.useMemo(() => (text ? qrSvgPath(qrMatrix(text), 4) : null), [text]);
  if (!text) return <div aria-hidden style={{ width: size, height: size, borderRadius: LL.r.md, background: LL.surface2 }} />;
  if (!qr) {
    return (
      <div style={{ width: size, height: size, borderRadius: LL.r.md, background: LL.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, boxSizing: 'border-box', textAlign: 'center', ...meta }}>
        This program is too large for a QR code. Share the link instead.
      </div>
    );
  }
  return (
    <svg role="img" aria-label="QR code for this program's link" width={size} height={size} viewBox={`0 0 ${qr.size} ${qr.size}`} shapeRendering="crispEdges"
      style={{ display: 'block', borderRadius: LL.r.md, boxShadow: `0 0 0 1px ${LL.line}` }}>
      <rect width={qr.size} height={qr.size} fill="#fff" />
      <path d={qr.d} fill="#111" />
    </svg>
  );
}

// ——— Share sheet ——————————————————————————————————————————————————————————

export function ShareProgramSheet({ open, onClose, program }) {
  const { state, toast } = useApp();
  const [link, setLink] = React.useState(null);
  const [err, setErr] = React.useState('');
  React.useEffect(() => {
    if (!open || !program) return undefined;
    let alive = true;
    setLink(null); setErr('');
    encodeProgramLink(program, { exercises: state.exercises })
      .then(r => { if (alive) setLink(r); })
      .catch(() => { if (alive) setErr('Could not build a link for this program.'); });
    return () => { alive = false; };
  }, [open, program, state.exercises]);
  const copy = async () => {
    if (!link) return;
    const ok = await copyText(link.url);
    toast(ok ? 'Link copied' : 'Could not copy: select the link and copy it');
  };
  const share = async () => {
    if (!link) return;
    if (canShare()) {
      try { await navigator.share({ title: program.name, text: `${program.name}: a training program for Lift Log`, url: link.url }); return; } catch (e) { if (e && e.name === 'AbortError') return; }
    }
    copy();
  };
  if (!program) return null;
  const nEx = (program.days || []).reduce((a, d) => a + (d.exercises?.length || 0), 0);
  return (
    <LLSheet open={open} onClose={onClose} title="Share program" subtitle={program.name} maxHeight="92%">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, paddingTop: 4 }}>
        <div style={{ padding: 10, borderRadius: LL.r.lg, background: '#fff' }}>
          <QrCode text={link?.url} />
        </div>
        <div style={{ ...meta, textAlign: 'center', lineHeight: 1.45, maxWidth: 320 }}>
          {program.days?.length || 0} days · {nEx} exercises{program.cycle ? ` · ${program.cycle.weeks.length}-week cycle` : ''}. Scan with a phone camera or send the link.
          {' '}Training maxes, weights and your history stay on this phone.
        </div>
        {err && <div role="alert" style={{ ...meta, color: LL.danger }}>{err}</div>}
        <button type="button" onClick={copy} disabled={!link} aria-label="Copy link" style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '0 12px', height: 46, borderRadius: LL.r.md, cursor: link ? 'pointer' : 'default',
          background: LL.surface2, border: `1px solid ${LL.line}`, color: LL.fgMute, fontFamily: LL.ui, fontSize: LL.fs.meta, textAlign: 'left',
        }}>
          <PIcon.link style={{ width: 16, height: 16, flexShrink: 0, color: LL.fgDim }} />
          <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{link ? link.url : 'Building link…'}</span>
          <span style={{ color: LL.accentText, fontWeight: 600, flexShrink: 0 }}>Copy</span>
        </button>
        <Btn full size="lg" onClick={share} disabled={!link}>{canShare() ? 'Share link' : 'Copy link'}</Btn>
      </div>
    </LLSheet>
  );
}

// ——— Paste a link ——————————————————————————————————————————————————————————

export function ImportLinkSheet({ open, onClose, onPreview }) {
  const [text, setText] = React.useState('');
  React.useEffect(() => { if (open) setText(''); }, [open]);
  const token = tokenFromText(text);
  const paste = async () => {
    try { const t = await navigator.clipboard.readText(); if (t) setText(t); } catch { /* permission denied: the field still works */ }
  };
  return (
    <LLSheet open={open} onClose={onClose} title="Import a program" subtitle="From a shared link">
      <div style={{ ...meta, lineHeight: 1.45, marginBottom: 10 }}>Paste a Lift Log program link. You'll see the whole program before anything is added.</div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="https://…#p=…" aria-label="Program link"
        style={{
          width: '100%', boxSizing: 'border-box', padding: 12, borderRadius: LL.r.md, resize: 'none',
          background: LL.surface2, color: LL.fg, border: `1px solid ${text && !token ? `color-mix(in oklab, ${LL.danger} 60%, ${LL.line})` : LL.line}`,
          fontFamily: LL.ui, fontSize: LL.fs.body, outline: 'none', lineHeight: 1.4, overflowWrap: 'anywhere',
        }} />
      <div style={{ minHeight: 20, ...meta, fontSize: LL.fs.sm, marginTop: 6, color: text && !token ? `color-mix(in oklab, ${LL.danger} 80%, ${LL.fg})` : LL.fgDim }}>
        {text && !token ? 'That doesn\'t look like a program link.' : token ? 'Link recognised.' : ''}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <Btn tone="secondary" onClick={paste} style={{ flex: 1 }}>Paste</Btn>
        <Btn onClick={() => token && onPreview(token)} disabled={!token} style={{ flex: 2 }}>Preview program</Btn>
      </div>
    </LLSheet>
  );
}

// ——— Preview + add ——————————————————————————————————————————————————————————

function thumbFor(ex, catalog) {
  if (!ex) return null;
  const entry = ex.img ? ex : catalogEntryFor(ex.id, catalog);
  return { img: entry?.img || null, primary: ex.primary || entry?.primary || (ex.group ? [ex.group] : []), secondary: ex.secondary || entry?.secondary || [] };
}

export function SharedProgramSheet({ token, onClose, onAdded }) {
  const { state, actions, toast, setRoute } = useApp();
  const catalog = useCatalog();
  const [shared, setShared] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  // A ref, not state: two taps in the same frame both see busy === false.
  const addingRef = React.useRef(false);
  React.useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    setShared(null); setErr('');
    decodeProgramToken(token).then(s => { if (alive) setShared(s); }).catch(e => { if (alive) setErr(e.message || 'This link is damaged.'); });
    return () => { alive = false; };
  }, [token]);
  const key = React.useMemo(() => (shared ? sharedProgramKey(shared) : null), [shared]);
  const existing = findSharedCopy(state.programs, key);
  const built = React.useMemo(() => (shared ? materializeShared(shared, { exercises: state.exercises, catalog, catalogToExercise, resolveName: resolveTemplateExercise }) : null), [shared, state.exercises, catalog]);
  const openExisting = () => {
    if (!existing) return;
    const id = existing.id;
    onClose && onClose();
    setRoute('program-edit', { programId: id });
  };
  const add = async () => {
    if (!shared || addingRef.current) return;
    // Same program link twice: never a second copy.
    if (findSharedCopy(state.programs, key)) { openExisting(); return; }
    addingRef.current = true;
    setBusy(true);
    try {
      const cat = catalog || await loadCatalog().catch(() => null);
      const { program, newExercises } = materializeShared(shared, { exercises: state.exercises, catalog: cat, catalogToExercise, resolveName: resolveTemplateExercise });
      if (newExercises.length) actions.ensureExercises(newExercises);
      const prev = state.programs.some(p => p.id === state.activeProgramId) ? state.activeProgramId : null;
      const id = actions.addProgram({ ...program, shared: { key, at: Date.now() } }, { silent: true });
      if (prev) {
        actions.setActiveProgram(prev);
        toast(`${program.name} added to your programs`, 'default', { action: { label: 'Make active', run: () => actions.setActiveProgram(id) } });
      } else toast(`${program.name} is your program`);
      onAdded && onAdded(id, !prev);
    } finally { setBusy(false); setTimeout(() => { addingRef.current = false; }, 400); }
  };

  const open = !!token;
  if (err) {
    return (
      <LLSheet open={open} onClose={onClose} title="Can't open this program" subtitle="Shared link">
        <div style={{ ...meta, lineHeight: 1.5 }}>{err} Ask for the link again, or check that it was copied in full.</div>
        <Btn full tone="secondary" onClick={onClose} style={{ marginTop: 16 }}>Close</Btn>
      </LLSheet>
    );
  }
  const p = built?.program;
  const report = built?.report;
  const exById = new Map([...(state.exercises || []), ...(built?.newExercises || [])].map(e => [e.id, e]));
  const nEx = p ? p.days.reduce((a, d) => a + d.exercises.length, 0) : 0;
  const setsWk = p ? p.days.reduce((a, d) => a + d.exercises.reduce((b, pe) => b + (pe.sets || 0), 0), 0) : 0;
  const schemes = p ? [...new Set(p.days.flatMap(d => d.exercises.map(pe => schemeOf(pe))))].filter(s => s !== 'double') : [];
  const cyc = p?.cycle ? cycleOf(p) : null;
  const newCount = built ? built.newExercises.length : 0;
  return (
    <Sheet open={open} onClose={onClose} title={p ? p.name : 'Loading program…'} subtitle="Shared program"
      footer={existing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div role="status" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, ...meta, fontSize: LL.fs.sm, color: LL.fgMute }}>
            <LLIcon.check style={{ width: 14, height: 14, color: LL.accentText }} />
            Already in your programs{existing.id === state.activeProgramId ? ' · active' : ''}
          </div>
          <Btn full size="lg" onClick={openExisting}>Open {existing.name}</Btn>
        </div>
      ) : <Btn full size="lg" onClick={add} disabled={!p || busy}>{busy ? 'Adding…' : 'Add to my programs'}</Btn>}>
      {!p ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 6 }} aria-busy="true">
          {[90, 160, 220].map(h => <div key={h} style={{ height: h, borderRadius: LL.r.md, background: LL.surface }} />)}
        </div>
      ) : (
        <>
          {p.description && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fgMute, lineHeight: 1.5, marginBottom: 14 }}>{p.description}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <StatTile label="Days / wk" value={p.days.length} />
            <StatTile label="Exercises" value={nEx} />
            <StatTile label="Sets / wk" value={setsWk} />
          </div>
          <div style={{ marginTop: 14, ...card, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={kicker}>Weekly schedule</div>
              {p.split && <span style={{ ...meta, fontSize: LL.fs.sm }}>{p.split}</span>}
            </div>
            <SplitStrip days={p.days.map(d => d.day).filter(Boolean)} size="lg" />
          </div>
          <div style={{ marginTop: 14 }}>
            <div style={{ ...kicker, margin: '0 2px 8px' }}>How it progresses</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {schemes.length ? schemes.map(s => <Pill key={s} tone="accent">{SCHEMES[s]?.label || s}</Pill>) : <Pill tone="accent">Double progression</Pill>}
              {cyc && <Pill>{cyc.weeks.length}-week cycle{cyc.weeks.some(w => w.deload) ? ' · deload' : ''}{cyc.repeat ? '' : ' · ends'}</Pill>}
            </div>
            {cyc && (
              <div className="llp-noscroll" style={{ display: 'flex', gap: 6, overflowX: 'auto', margin: '10px -16px 0', padding: '0 16px 2px', scrollbarWidth: 'none' }}>
                {cyc.weeks.map((w, i) => (
                  <span key={i} style={{ flexShrink: 0, padding: '6px 10px', borderRadius: LL.r.sm, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, whiteSpace: 'nowrap', background: w.deload ? 'transparent' : LL.surface, color: w.deload ? LL.fgMute : LL.fg, border: `1px ${w.deload ? 'dashed' : 'solid'} ${LL.line2}` }}>
                    {w.deload ? `Wk ${i + 1} · deload` : (w.label || `Week ${i + 1}`).replace(/^Week (\d+)/, 'Wk $1')}
                  </span>
                ))}
              </div>
            )}
          </div>
          {(newCount > 0) && (
            <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: LL.r.md, background: LL.surface, border: `1px solid ${LL.line}` }}>
              <span style={{ width: 22, height: 22, borderRadius: LL.r.pill, background: LL.accentSoft, color: LL.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><PIcon.plus style={{ width: 14, height: 14 }} /></span>
              <div style={{ ...meta, color: LL.fg, lineHeight: 1.45 }}>
                {newCount - report.created > 0 && <div>{newCount - report.created} exercise{newCount - report.created === 1 ? '' : 's'} will be added to your library from the catalog.</div>}
                {report.created > 0 && <div>New custom exercise{report.created === 1 ? '' : 's'}: {report.createdNames.join(', ')}.</div>}
              </div>
            </div>
          )}
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {p.days.map(d => (
              <div key={d.id} style={{ ...card, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: `1px solid ${LL.line}` }}>
                  <div style={{ width: 42, height: 42, borderRadius: LL.r.md, background: d.day ? LL.accentSoft : LL.surface2, color: d.day ? LL.accentText : LL.fgDim, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>{d.day || '—'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.fg }}>{d.name}</div>
                    <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 1 }}>{d.exercises.length} exercise{d.exercises.length === 1 ? '' : 's'}</div>
                  </div>
                </div>
                <div style={{ padding: '4px 0' }}>
                  {d.exercises.map((pe, i) => {
                    const ex = exById.get(pe.exerciseId);
                    const s = schemeOf(pe);
                    const line = s === 'double'
                      ? `${pe.sets} × ${fmtReps(pe.repMin, pe.repMax)}${pe.unit === 'sec' ? ' s' : ''}`
                      : rowsSummary(prescribe({ program: p, pe, weekIndex: 0 }).rows, 'lb', { weights: false });
                    const prev = d.exercises[i - 1], next = d.exercises[i + 1];
                    const inSS = !!pe.supersetId;
                    return (
                      <div key={pe.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', position: 'relative' }}>
                        {inSS && <div style={{ position: 'absolute', left: 5, top: prev?.supersetId === pe.supersetId ? 0 : '50%', bottom: next?.supersetId === pe.supersetId ? 0 : '50%', width: 3, borderRadius: LL.r.xs, background: LL.accent, opacity: 0.8 }} />}
                        <ExerciseThumb item={thumbFor(ex, catalog)} size={40} radius={LL.r.sm} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg, lineHeight: 1.25, overflowWrap: 'anywhere' }}>{pe.name}</div>
                          <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 2, lineHeight: 1.35 }}>{line} · rest {fmtRest(pe.rest ?? 120)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div style={{ ...meta, fontSize: LL.fs.sm, color: LL.fgDim, textAlign: 'center', marginTop: 16, lineHeight: 1.45 }}>
            Shared links carry the plan only. Starting weights and training maxes are yours to set.
          </div>
        </>
      )}
    </Sheet>
  );
}

export { QrCode };
